import api, { API } from './client';

// Build absolute media URL for a post's media_url.
// The browser cannot send Authorization headers via <img>/<video>/<audio> tags,
// so we attach the JWT as a query parameter — the backend accepts either source.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const mediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const base = `${BACKEND_URL}${path}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('ig_token') : null;
  if (!token) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(token)}`;
};

// Phase 51 · Media performance — request a resized WebP thumbnail from the
// backend. Widths are snapped to the closest bucket server-side; anything
// > 1600 falls back to the original. Video/audio paths are returned as-is.
const THUMB_WIDTHS = [320, 480, 640, 800, 1080];
const isImagePath = (p) => /\.(png|jpe?g|gif|webp|heic)(\?|$)/i.test(p || '');

export const thumbUrl = (path, width) => {
  if (!path || !isImagePath(path)) return mediaUrl(path);
  const w = Math.max(1, Math.floor(width || 0));
  const base = mediaUrl(path);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}w=${w}`;
};

/**
 * Build a `srcset` string covering the standard thumbnail buckets so
 * the browser can pick the best size for the current viewport DPR.
 */
export const thumbSrcSet = (path) => {
  if (!path || !isImagePath(path)) return undefined;
  return THUMB_WIDTHS.map((w) => `${thumbUrl(path, w)} ${w}w`).join(', ');
};

// Phase 31 — wallet-first purchase of a paid post.
// On 402 the server returns `insufficient_balance|...` so the caller can
// open the InsufficientBalanceDialog.
export const buyPost = async (postId, clientRef) => {
  const res = await api.post(`/posts/${postId}/buy`, clientRef ? { client_ref: clientRef } : {});
  return res.data;
};

export const createPost = async ({ file, caption = '', location = '', is_paid = false, price = 0, preview_description = '', media_width = 0, media_height = 0, title = '', duration_seconds = 0, category_slug = '', onProgress }) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('title', title);
  fd.append('category_slug', category_slug);
  fd.append('duration_seconds', String(Math.max(0, Math.round(duration_seconds || 0))));
  fd.append('caption', caption);
  fd.append('location', location);
  fd.append('is_paid', is_paid ? 'true' : 'false');
  fd.append('price', String(price));
  fd.append('preview_description', preview_description);
  fd.append('media_width', String(media_width || 0));
  fd.append('media_height', String(media_height || 0));
  const res = await api.post('/posts', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    },
  });
  return res.data;
};

// Phase 84 — chunked/resumable upload for big videos (up to 500MB).
// 10MB chunks, sequential, 3 retries per chunk, real % progress.
// Giu 2026 — RESUME: the session id is remembered per file fingerprint
// (name|size|lastModified) so a network drop / closed tab continues from the
// missing chunk (server keeps sessions 24h) instead of restarting from zero.
const CHUNK_SIZE = 10 * 1024 * 1024;
const PENDING_KEY = 'nymia_pending_uploads';

const fingerprint = (file) => `${file.name || ''}|${file.size}|${file.lastModified || 0}`;
const readPending = () => { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '{}'); } catch (_e) { return {}; } };
const writePending = (map) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify(map)); } catch (_e) { /* noop */ } };
const rememberPending = (file, uploadId) => { const m = readPending(); m[fingerprint(file)] = { upload_id: uploadId, at: Date.now() }; writePending(m); };
export const forgetPending = (file) => { const m = readPending(); delete m[fingerprint(file)]; writePending(m); };

// Returns {upload_id, received, size, next_index, percent} when a resumable
// server session exists for this exact file, else null (stale entries are dropped).
export const getResumableUpload = async (file) => {
  const entry = readPending()[fingerprint(file)];
  if (!entry?.upload_id) return null;
  try {
    const { data } = await api.get(`/uploads/${entry.upload_id}`);
    if (data.size !== file.size || data.received <= 0) { forgetPending(file); return null; }
    return { ...data, percent: Math.min(99, Math.round((data.received / data.size) * 100)) };
  } catch (_e) {
    forgetPending(file);
    return null;
  }
};

const isDefinitive = (err) => [400, 403, 404, 409, 413].includes(err?.response?.status);

export const createPostChunked = async ({ file, caption = '', location = '', is_paid = false, price = 0, preview_description = '', media_width = 0, media_height = 0, title = '', duration_seconds = 0, category_slug = '', onProgress, _restarted = false }) => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadId;
  let startIndex = 0;
  const resumable = _restarted ? null : await getResumableUpload(file);
  if (resumable) {
    uploadId = resumable.upload_id;
    startIndex = Math.min(resumable.next_index, totalChunks);
    if (onProgress) onProgress(resumable.percent);
  } else {
    const init = await api.post('/uploads/init', {
      filename: file.name || 'video.mp4',
      size: file.size,
      mime: file.type || 'application/octet-stream',
    });
    uploadId = init.data.upload_id;
    rememberPending(file, uploadId);
  }
  for (let i = startIndex; i < totalChunks; i += 1) {
    const blob = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
    let attempt = 0;
    for (;;) {
      try {
        const fd = new FormData();
        fd.append('index', String(i));
        fd.append('chunk', blob, 'chunk');
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/uploads/${uploadId}/chunk`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        break;
      } catch (err) {
        // Server-side staging lost (pod restart/replica) → restart the whole upload ONCE.
        if (!_restarted && String(err?.response?.data?.detail || '').startsWith('upload_storage_lost')) {
          forgetPending(file);
          return createPostChunked({ file, caption, location, is_paid, price, preview_description, media_width, media_height, title, duration_seconds, category_slug, onProgress, _restarted: true });
        }
        attempt += 1;
        if (attempt >= 3 || isDefinitive(err)) {
          if (isDefinitive(err)) {
            // Unrecoverable → drop the session; otherwise KEEP it for resume.
            forgetPending(file);
            try { await api.delete(`/uploads/${uploadId}`); } catch (_e) { /* noop */ }
          } else {
            err.resumable = true;
            err.message = 'Connessione interrotta: riprova, il caricamento riprenderà da dove si era fermato.';
          }
          throw err;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
    if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 99));
  }
  const fd = new FormData();
  fd.append('caption', caption);
  fd.append('location', location);
  fd.append('is_paid', is_paid ? 'true' : 'false');
  fd.append('price', String(price));
  fd.append('preview_description', preview_description);
  fd.append('media_width', String(media_width || 0));
  fd.append('media_height', String(media_height || 0));
  fd.append('title', title);
  fd.append('duration_seconds', String(Math.max(0, Math.round(duration_seconds || 0))));
  fd.append('category_slug', category_slug);
  let res;
  try {
    res = await api.post(`/uploads/${uploadId}/complete`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  } catch (err) {
    // A failed complete on a finished session is unrecoverable by resume → forget it.
    if (isDefinitive(err)) forgetPending(file);
    throw err;
  }
  forgetPending(file);
  if (onProgress) onProgress(100);
  return res.data;
};

// Phase F6 — Racconto Testuale: plain JSON, no file.
export const createTextPost = async ({ title, body_text, caption = '', is_paid = false, price = 0, preview_description = '', category_slug = '' }) => {
  const res = await api.post('/posts/text', { title, body_text, caption, is_paid, price, preview_description, category_slug });
  return res.data;
};

export const fetchFeed = async ({ limit = 30, skip = 0 } = {}) => {
  const res = await api.get('/posts/feed', { params: { limit, skip } });
  return res.data;
};

export const fetchUserPosts = async (username) => {
  const res = await api.get(`/posts/user/${username}`);
  return res.data;
};

export const fetchPost = async (id) => {
  const res = await api.get(`/posts/${id}`);
  return res.data;
};

export const deletePost = async (id) => {
  const res = await api.delete(`/posts/${id}`);
  return res.data;
};

export const updatePost = async (id, patch) => {
  const res = await api.patch(`/posts/${id}`, patch);
  return res.data;
};

export { API };
